'use client'
import { useEffect, useRef, useCallback, useState } from 'react'
import Script from 'next/script'

/**
 * --------------------------------------------------------------------------
 * CmsEmbed
 *
 * Safely render CMS-provided embed code that includes external and inline scripts.
 * External scripts load first; inline scripts execute after they finish.
 *
 * @param {string} html - The raw HTML embed code from CMS
 * --------------------------------------------------------------------------
 **/

/** Extract unique external script URLs from an HTML string (client-side only). */
function parseExternalScripts(html: string): string[] {
  const temp = document.createElement('div')
  temp.innerHTML = html
  const urls = Array.from(temp.querySelectorAll('script[src]')).map(
    (s) => s.getAttribute('src')!,
  )
  return [...new Set(urls)]
}

export default function ParseHtmlClient({ html }: { html: string }) {
  const containerRef = useRef<HTMLDivElement>(null)
  const inlineScriptsRanRef = useRef(false)
  const loadedCountRef = useRef(0)

  // Lazy initializer only runs on the client (not during SSR).
  const [externalScripts] = useState<string[]>(() => {
    if (typeof document === 'undefined') return []
    return parseExternalScripts(html)
  })

  // Inject non-script HTML into the container on mount
  useEffect(() => {
    if (!html || !containerRef.current) return

    const temp = document.createElement('div')
    temp.innerHTML = html
    temp.querySelectorAll('script').forEach((node) => node.remove())
    containerRef.current.innerHTML = temp.innerHTML
  }, [html])

  // Run inline scripts exactly once, inside the container
  const runInlineScripts = useCallback(() => {
    if (inlineScriptsRanRef.current || !containerRef.current) return
    inlineScriptsRanRef.current = true

    const temp = document.createElement('div')
    temp.innerHTML = html

    Array.from(temp.querySelectorAll('script:not([src])')).forEach((oldScript) => {
      const newScript = document.createElement('script')
      Array.from(oldScript.attributes).forEach((attr) =>
        newScript.setAttribute(attr.name, attr.value),
      )
      newScript.textContent = oldScript.textContent
      containerRef.current!.appendChild(newScript)
    })
  }, [html])

  // If no external scripts exist, run inline scripts on mount
  useEffect(() => {
    if (externalScripts.length === 0) {
      runInlineScripts()
    }
  }, [externalScripts, runInlineScripts])

  // Only run inline scripts after ALL external scripts have loaded
  const handleScriptLoad = useCallback(() => {
    loadedCountRef.current += 1
    if (loadedCountRef.current >= externalScripts.length) {
      runInlineScripts()
    }
  }, [externalScripts, runInlineScripts])

  return (
    <>
      {/* Load external scripts; inline scripts run after ALL have loaded */}
      {externalScripts.map((src) => (
        <Script key={src} src={src} strategy="afterInteractive" onLoad={handleScriptLoad} />
      ))}

      {/* Container for the non-script HTML content */}
      <div ref={containerRef} />
    </>
  )
}
